import { IUser } from '@sravni/types/lib/auth';

import { VotesForReviewsListParams } from '../__generated__/reviews/reviewsApiTypes';
import { REVIEW_FIELDS } from '../components/CreateReview/constants/common';
import { getPersonalDataFromChanges } from '../components/CreateReview/helpers/getPersonalDataFromChanges';
import { TFormInputs } from '../components/CreateReview/types';
import { FILTERS } from '../constants/reviews';
import { OrganizationType } from '../interfaces/organizations';
import {
  IAdditionalInfo,
  IAdditionalInfoQuery,
  IAggregatedReviewsCountQuery,
  ICompanyResponse,
  ICompanyResponseQuery,
  IRepresentative,
  IResolveBody,
  IReviewCreateBody,
  IReviewFilters,
  IReviewItem,
  IReviewParams,
  IReviewsCountQuery,
  IReviewsList,
  IReviewUser,
  IUserActivityQuery,
  IUserActivityResponse,
  IViewsResult,
  IVoteBody,
  IVotesByReviewId,
} from '../interfaces/reviews';
import { getAuthHeader } from '../utils/auth';

import ProxyClient from './clients/ProxyClient';

export const loadReviewsList = async (params: FILTERS) => {
  const { data } = await ProxyClient.get<IReviewsList>('/reviews', { params });
  return data;
};

export const loadReviewItem = async (params: IReviewParams, user?: IUser) => {
  const { id, fingerPrint } = params;

  const { data } = await ProxyClient.get<IReviewItem>(`/review/${id}`, {
    params: { fingerPrint },
    ...(user && { headers: getAuthHeader(user) }),
  });

  return data;
};

export const createReview = async (requestData: IReviewCreateBody) => {
  const { data } = await ProxyClient.post<number>(`/review/create`, requestData);
  return data;
};

export const updateReview = async ({ data, initData }: { data: IReviewCreateBody; initData: TFormInputs }) => {
  const [personalData, isNotOnlyPersonalDataChanged] = getPersonalDataFromChanges({ data, initData });

  if (isNotOnlyPersonalDataChanged) {
    await ProxyClient.put(`/review/update`, data);
  } else {
    await ProxyClient.put(`/review/update-personal-data`, {
      ...personalData,
      id: initData[REVIEW_FIELDS.ID],
      reviewObjectId: data.reviewObjectId,
    });
  }
};

export const deleteReview = async (id: number) => {
  await ProxyClient.delete(`/review/delete/${id}`);
};

export const resolveReview = async (data: IResolveBody) => {
  await ProxyClient.put(`/review/resolve`, data);
};

export const voteReview = async (data: IVoteBody) => {
  return await ProxyClient.post('/review/vote', data);
};

export const getVotes = async (params: VotesForReviewsListParams) => {
  return await ProxyClient.post<IVotesByReviewId>('/votes', params);
};

export const increaseViews = async (id: number) => {
  return await ProxyClient.patch(`/review/increase-views/${id}`);
};

export const loadViews = async (params: { ids: number[] }) => {
  const { data } = await ProxyClient.get<IViewsResult[]>('/review/views', { params });
  return data;
};

export const loadAdditionalInfo = async (params: IAdditionalInfoQuery) => {
  const { data } = await ProxyClient.get<IAdditionalInfo>(`/reviews/additional-info`, { params });
  return data;
};

export const loadUserInfo = async () => {
  try {
    const { data } = await ProxyClient.get<IReviewUser>(`/reviews/user-info`);
    return data;
  } catch (e) {
    return null;
  }
};

export const loadOrganizationResponse = async (params: ICompanyResponseQuery) => {
  const { data } = await ProxyClient.get<ICompanyResponse>(`/reviews/organizationResponse/${params.organizationId}`, {
    params,
  });
  return data;
};

export const loadRepresentative = async (id: number) => {
  const { data } = await ProxyClient.get<IRepresentative>(`/reviews/representative/${id}`);
  return data;
};

export const loadFilters = async (params: { objectType: OrganizationType; isLegal?: boolean }) => {
  const { objectType, isLegal } = params;
  const { data } = await ProxyClient.get<IReviewFilters>(`/reviews/filters/${objectType}`, {
    params: { isLegal },
    timeout: 5000,
  });
  return data;
};

export const loadAllFilters = async () => {
  const { data } = await ProxyClient.get(`/reviews/allFilters`, { timeout: 5000 });
  return data;
};

export const loadReviewsCount = async (params: IReviewsCountQuery) => {
  const { data } = await ProxyClient.get(`/reviews/count`, { params });
  return data;
};
export const loadAggregatedReviewsCount = async (params: IAggregatedReviewsCountQuery) => {
  const { data } = await ProxyClient.get(`/reviews/aggregatedCount`, { params });
  return data;
};

export const loadUserActivity = async (params: Omit<IUserActivityQuery, 'userId'>) => {
  const { data } = await ProxyClient.get<IUserActivityResponse>(`/reviews/userActivity`, { params });
  return data;
};
